import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sunrise, Check, Newspaper, TrendingUp, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

const STORAGE_KEY = "fwd_daily_briefing_dismissed_at";
const DELAY_MS = 30_000;
// Don't re-show for 7 days after dismissal/subscription
const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Daily Briefing email pop-up.
 * - Appears once after 30s of session activity
 * - Suppressed for 7 days after dismissal or successful signup
 * - Suppressed entirely on /admin routes
 */
const DailyBriefingPopup = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.pathname.startsWith("/admin")) return;

    try {
      const dismissedAt = Number(localStorage.getItem(STORAGE_KEY) || 0);
      if (dismissedAt && Date.now() - dismissedAt < COOLDOWN_MS) return;
    } catch {
      // localStorage may be unavailable — fall through and just show
    }

    const timer = window.setTimeout(() => setOpen(true), DELAY_MS);
    return () => window.clearTimeout(timer);
  }, []);

  const persistDismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      // ignore
    }
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      persistDismiss();
      // Reset shortly after close so a re-open in dev is clean
      window.setTimeout(() => {
        setEmail("");
        setSuccess(false);
      }, 200);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    const { error } = await supabase.from("newsletter_subscribers").insert({
      email: email.trim(),
      source: "daily_briefing_popup",
    });
    setSubmitting(false);
    if (error) {
      if (error.code === "23505") {
        toast.info("You're already on the briefing list.");
        persistDismiss();
        setSuccess(true);
        return;
      }
      toast.error("Couldn't sign you up. Please try again.");
      return;
    }
    persistDismiss();
    setSuccess(true);
    toast.success("You're in! The next briefing lands tomorrow at 7am.");
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md glass border-primary/20">
        <DialogHeader>
          <div className="mx-auto h-12 w-12 rounded-full bg-primary/15 flex items-center justify-center mb-2">
            <Sunrise className="h-6 w-6 text-primary" aria-hidden="true" />
          </div>
          <DialogTitle className="font-display text-2xl text-center">
            {success ? "You're on the list" : "The Daily Briefing"}
          </DialogTitle>
          <DialogDescription className="text-center">
            {success
              ? "Tomorrow morning, your first briefing arrives at 7am."
              : "One email. Every weekday at 7am. The 5 stories shaping forex, fintech & prop trading."}
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="flex flex-col items-center gap-3 py-2">
            <div className="h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center">
              <Check className="h-6 w-6 text-primary" />
            </div>
            <Button size="sm" variant="outline" onClick={() => handleOpenChange(false)} className="rounded-full">
              Close
            </Button>
          </div>
        ) : (
          <>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <Newspaper className="h-4 w-4 mt-0.5 text-primary shrink-0" aria-hidden="true" />
                <span>Top 5 stories curated by our editors</span>
              </li>
              <li className="flex items-start gap-2">
                <TrendingUp className="h-4 w-4 mt-0.5 text-primary shrink-0" aria-hidden="true" />
                <span>Pre-market FX & crypto snapshot</span>
              </li>
              <li className="flex items-start gap-2">
                <ShieldAlert className="h-4 w-4 mt-0.5 text-primary shrink-0" aria-hidden="true" />
                <span>Active scam alerts & regulator actions</span>
              </li>
            </ul>
            <form onSubmit={handleSubmit} className="space-y-3 pt-2">
              <Input
                type="email"
                required
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11 bg-muted/50 border-border"
                aria-label="Email address"
              />
              <Button type="submit" size="lg" className="w-full rounded-full" disabled={submitting}>
                {submitting ? "Signing you up…" : "Get the Daily Briefing"}
              </Button>
              <p className="text-[10px] text-muted-foreground/70 text-center">
                Free. Unsubscribe anytime. No spam — ever.
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DailyBriefingPopup;
